import { useCallback, useMemo } from 'react';
import { FieldPath, useFormContext, useWatch } from 'react-hook-form';

import {
  ReferenceCandidateAdapter,
  ReferenceTypeOption,
} from 'components/ReferenceCandidates/types';
import { appendReferenceValue } from 'components/ReferenceCandidates/utils';

import {
  PlsFormValues,
  PlsOperationalFieldDefinition,
  PlsReferenceFieldDefinition,
} from '../types';

type PlsReferenceCandidateAdapterArgs = {
  loadReferenceFields: PlsReferenceFieldDefinition[];
  operationalFields: PlsOperationalFieldDefinition[];
  // Opens a Load Reference Fields card holding the number, for the rep to pick its type.
  startReference: (number: string) => void;
};

// Lets reference candidates become PLS references. The types are PLS's load reference catalog
// plus the bill-to's customer reference fields; a customer field code is written to the
// Customer Reference Fields section, every other code to Load Reference Fields.
export function usePlsReferenceCandidateAdapter({
  loadReferenceFields,
  operationalFields,
  startReference,
}: PlsReferenceCandidateAdapterArgs): ReferenceCandidateAdapter {
  const { control, getValues, setValue } = useFormContext<PlsFormValues>();
  const [
    loadReferenceValues,
    operationalValues,
    pickupReferenceValues,
    consigneeReferenceValues,
    stops,
    poNums,
    customerRefNumber,
    pickupRefNumber,
    consigneeRefNumber,
  ] = useWatch({
    control,
    name: [
      'plsLoadReferenceFields',
      'plsOperationalFields',
      'plsPickupReferenceFields',
      'plsConsigneeReferenceFields',
      'stops',
      'poNums',
      'customer.refNumber',
      'pickup.refNumber',
      'consignee.refNumber',
    ],
  });

  const referenceTypes = useMemo(
    () =>
      toReferenceTypeOptions([...operationalFields, ...loadReferenceFields]),
    [loadReferenceFields, operationalFields]
  );

  const operationalCodesByKey = useMemo(
    () =>
      new Map(
        operationalFields
          .filter((field) => field.code?.trim())
          .map((field) => [field.code.trim().toLowerCase(), field.code.trim()])
      ),
    [operationalFields]
  );

  const usedNumbers = useMemo(
    () => [
      ...Object.values(loadReferenceValues ?? {}),
      ...Object.values(operationalValues ?? {}),
      ...Object.values(pickupReferenceValues ?? {}),
      ...Object.values(consigneeReferenceValues ?? {}),
      ...(stops ?? []).map((stop) => stop?.refNumber ?? ''),
      poNums ?? '',
      customerRefNumber ?? '',
      pickupRefNumber ?? '',
      consigneeRefNumber ?? '',
    ],
    [
      loadReferenceValues,
      operationalValues,
      pickupReferenceValues,
      consigneeReferenceValues,
      stops,
      poNums,
      customerRefNumber,
      pickupRefNumber,
      consigneeRefNumber,
    ]
  );

  const addReference = useCallback(
    (number: string, code: string) => {
      const operationalCode = operationalCodesByKey.get(code.toLowerCase());
      // A customer reference field holds one value, so the picked number replaces it; a number
      // it replaces goes back to "Other numbers from the email". Load reference fields are lists.
      if (operationalCode) {
        setValue(
          `plsOperationalFields.${operationalCode}` as FieldPath<PlsFormValues>,
          number.trim(),
          { shouldDirty: true }
        );
        return;
      }

      const fieldName =
        `plsLoadReferenceFields.${code}` as FieldPath<PlsFormValues>;
      const current = getValues(fieldName);
      setValue(
        fieldName,
        appendReferenceValue(
          typeof current === 'string' ? current : '',
          number
        ),
        { shouldDirty: true }
      );
    },
    [getValues, operationalCodesByKey, setValue]
  );

  return { referenceTypes, usedNumbers, addReference, startReference };
}

// One option per code, first definition wins, so a code in both catalogs is listed once.
function toReferenceTypeOptions(
  definitions: { code: string; name: string }[]
): ReferenceTypeOption[] {
  const seen = new Set<string>();
  const options: ReferenceTypeOption[] = [];
  definitions.forEach((definition) => {
    const code = definition.code?.trim();
    if (!code || seen.has(code.toLowerCase())) {
      return;
    }
    seen.add(code.toLowerCase());
    options.push({ value: code, label: definition.name?.trim() || code });
  });
  return options;
}
